import React, { memo, useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import { LocaleAlternate } from '@/utilites/seo/locale-alternates';
import { getStoreMeta } from '@/constants/retail-stores.constants';
import SearchProvider from '../SearchProvider';
import CustomSearchBox from './searchCustom/VirtualSearchBox';
import { Configure } from 'react-instantsearch';
import { filterBySlugs, SEARCH_HITS_PER_PAGE } from '@/constants/search.constants';

interface SellerSearchLayoutProps {
  children: React.ReactNode;
  sellerName: string;
  skuSlugs: string[];
  /** Where this page lives in each language it actually has. */
  localeAlternates?: LocaleAlternate[];
  /** Strapi `seller_id`; brings in the shop's own search-result copy, if any. */
  sellerId?: string | null;
  /** The landing page's own SEO copy (WEB-1639); wins over every default. */
  metaTitle?: string | null;
  metaDescription?: string | null;
  metaKeywords?: string | null;
}

const SellerSearchLayout = ({
  children,
  sellerName,
  skuSlugs,
  localeAlternates,
  sellerId,
  metaTitle,
  metaDescription,
  metaKeywords,
}: SellerSearchLayoutProps) => {
  const router = useRouter();

  // Restrict to this seller's bottles. Typesense `filter_by` syntax — the adapter forwards the
  // string verbatim. An empty list must match nothing, which `filterBySlugs` guarantees; the old
  // code returned '' here, i.e. no filter at all, showing the whole catalogue on a seller with
  // no listings.
  const objectIdFilter = useMemo(() => filterBySlugs(skuSlugs), [skuSlugs]);

  const englishAlternate = useMemo(
    () => localeAlternates?.find(({ locale }) => locale === 'en'),
    [localeAlternates]
  );

  // A shop landing page describes a real place, so it gets copy naming the city
  // and the street in the visitor's language. The generic fallback stays for
  // every other seller page — those have no such copy to write.
  const storeMeta = getStoreMeta(sellerId, router.locale || 'en');

  const pageTitle = metaTitle || storeMeta?.title || `Discover ${sellerName}'s offers`;
  const pageDescription =
    metaDescription ||
    storeMeta?.description ||
    `Browse ${sellerName}'s collection of premium spirits and rare bottles on Spiritory marketplace.`;
  const keywords =
    metaKeywords ||
    `spiritory, marketplace, buy, sell, ${sellerName}, ardbeg, rare whiskey, japanese, irish, bourbon`;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={getCanonicalUrl(router)} />
        {/* Only the languages this page actually has — a landing page slug is
            translated, so there is nothing to derive from the current path. */}
        {localeAlternates?.map(({ locale, path }) => (
          <link
            key={locale}
            rel="alternate"
            hrefLang={locale}
            href={`${process.env.NEXT_PUBLIC_BASE_URL}${path}`}
          />
        ))}
        {/* x-default is the fallback for unmatched languages, not a replacement
            for the self-referencing `en` tag — both belong in the head. */}
        {englishAlternate && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${process.env.NEXT_PUBLIC_BASE_URL}${englishAlternate.path}`}
          />
        )}
        <meta name="keywords" content={keywords} />
      </Head>

      <SearchProvider isSkusPage={true}>
        <section className="tw-container tw-mx-auto tw-max-w-7xl">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <CustomSearchBox />
            <Configure hitsPerPage={SEARCH_HITS_PER_PAGE} filters={objectIdFilter} />
            {children}
          </div>
        </section>
      </SearchProvider>
    </>
  );
};

export default memo(SellerSearchLayout);
